export interface OsNotificationOptions {
  body?: string;
  tag?: string;
  onClick?: () => void;
}

/**
 * Thin wrapper around the browser Notification API used to show operating-system
 * notification popups in addition to the in-app toast and sound. All methods are
 * safe to call on platforms that do not support notifications (they no-op).
 */
export class OsNotificationHelper {
  private static readonly icon = "/images/logos/logo_default.png";

  static isSupported(): boolean {
    return typeof window !== "undefined" && "Notification" in window;
  }

  static get permission(): NotificationPermission | null {
    return OsNotificationHelper.isSupported() ? Notification.permission : null;
  }

  /**
   * Asks the user for permission to show OS notifications. Must be triggered from
   * a user gesture (e.g. a click). Returns the resulting permission, or null when
   * notifications are not supported.
   */
  static async requestPermission(): Promise<NotificationPermission | null> {
    if (!OsNotificationHelper.isSupported()) {
      return null;
    }

    if (Notification.permission !== "default") {
      return Notification.permission;
    }

    try {
      return await Notification.requestPermission();
    } catch {
      return Notification.permission;
    }
  }

  /**
   * Shows an OS notification popup. Returns true when the popup was actually
   * displayed, and false when notifications are unsupported, permission has not
   * been granted, or construction failed — letting the caller fall back to an
   * in-app message.
   */
  static show(title: string, options: OsNotificationOptions = {}): boolean {
    if (!OsNotificationHelper.isSupported() || Notification.permission !== "granted") {
      return false;
    }

    try {
      const notification = new Notification(title, {
        body: options.body,
        tag: options.tag,
        icon: OsNotificationHelper.icon,
        // Re-alert (fresh popup + sound) even when a notification with the same
        // tag is still queued, instead of silently replacing it in the OS
        // notification centre. `renotify` requires a non-empty tag. It is a
        // valid runtime option but missing from the bundled DOM typings, hence
        // the cast.
        renotify: Boolean(options.tag),
      } as NotificationOptions & { renotify: boolean });

      notification.onclick = () => {
        window.focus();
        notification.close();
        options.onClick?.();
      };

      return true;
    } catch {
      // Notification construction can throw on some platforms — ignore silently.
      return false;
    }
  }
}
